// Carry Roll Demo datasets — pre-loaded, realistic store snapshots.
//
// Each dataset is a complete, valid store-state snapshot that exercises the
// full engine → store → render path. They double as integration-level smoke
// tests (see src/data/__tests__/demoDatasets.test.ts) and give later pipeline
// stages (UX hardening, accessibility, …) a meaningful working state to reason
// about instead of an empty shell.
//
// ── Dataset count justification (N = 6) ─────────────────────────────────────
// The engine exposes several distinct result modes / branching paths:
//   1. Fidelity branch      — Basic (closed-form reduced) vs Full (Verlet 3D)
//   2. Shot-shape branch    — Straight / Draw / Fade / Hook / Slice (spin axis)
//   3. Trajectory profile   — Low / Mid / High (apex/carry ratio)
//   4. Club-category branch — Driver / Wood / Hybrid / Iron / Wedge (roll %)
//   5. Environment branch   — wind, altitude, elevation, firmness, moisture,
//                              landing surface, humidity/pressure
//   6. Ball-type modifiers  — tour / urethane / ionomer / distance / soft
//   7. Delivery branch      — null vs populated (attack/loft/path/face/impact)
//   8. Landing-angle override — null vs explicit
//
// The three mandatory baseline cases:
//   • Minimal/simple   → "Simple Driver (Basic)"
//   • Typical mid      → "Tour Driver — Full Fidelity"
//   • Maximal/stress   → "Maximal Stress Conditions"
//
// Plus three additional datasets that cover branching paths the baselines do
// not exercise:
//   • Environment branch  → "Windy Highland Driver" (altitude + headwind + firm)
//   • Shape + club branch → "Mid-Iron Draw" (iron + draw curvature + delivery)
//   • Club + surface +    → "Pitching Wedge to Green" (wedge + green + high
//     profile branch        spin + fade + landing override)
//
// Together these cover: both fidelities, four shot shapes, four club
// categories, environment variants (wind/altitude/elevation/firmness/moisture),
// three ball types, two landing surfaces, populated + null delivery, and a
// landing-angle override. N is therefore 6.

import type {
  BagEntry,
  Session,
  SimulationRequest,
} from '@carry-roll/shared-types';
import {
  ClubCategory,
  Fidelity,
  Firmness,
  LandingSurface,
  SessionType,
  SurfaceMoisture,
} from '@carry-roll/shared-types';
import type { BallType } from '@carry-roll/shared-logic';
import { degToRad } from '@carry-roll/shared-logic';

/**
 * Structurally compatible with `SavedScenario` from store/compareSlice.
 * Defined locally so this data module depends only on the engine layer.
 */
export interface DemoScenarioRef {
  id: string;
  label: string;
  request: SimulationRequest;
}

/**
 * The loadable portion of the Zustand store. Every field is optional except the
 * primary `request` (it drives the CalculatorScreen and the memoized result).
 * Omitted collections are left untouched on load; `clearDemoData` resets them.
 */
export interface DemoDatasetState {
  request: SimulationRequest;
  sessions?: Session[];
  bag?: BagEntry[];
  scenarios?: DemoScenarioRef[];
}

export interface DemoDataset {
  /** Stable identifier used as React key + test selector. */
  id: string;
  /** Short label shown in the picker. */
  name: string;
  /** One-line description shown in the picker. */
  description: string;
  /** Store snapshot applied when this dataset is selected. */
  state: DemoDatasetState;
}

// ── Shared environment base (calm, sea-level, normal turf) ──────────────────
function calmEnv(): SimulationRequest['environment'] {
  return {
    altitudeM: 0,
    temperatureK: 293.15,
    windSpeed: 0,
    windDirDeg: 0,
    firmness: Firmness.Normal,
    moisture: SurfaceMoisture.Normal,
    landingSurface: LandingSurface.Fairway,
    landingSlopeDeg: 0,
    crossSlopeDeg: 0,
    elevationDiffM: 0,
    seaLevelBaseline: false,
    humidity: 0.5,
    pressurePa: 101325,
  };
}

// ── 1. Minimal / simple case — Basic fidelity driver, straight, calm ────────
const simpleDriver: SimulationRequest = {
  launch: {
    ballSpeed: 70, // ~157 mph
    launchAngle: degToRad(12.5),
    spinRpm: 3000,
    clubSpeed: 48,
    spinAxisDeg: 0,
    launchDirDeg: 0,
    apexHint: null,
  },
  delivery: null,
  environment: calmEnv(),
  override: { landingAngleDeg: null },
  club: { category: ClubCategory.Driver, loftDeg: 10.5, rollPercent: 0.2 },
  fidelity: Fidelity.Basic,
  ballType: 'tour',
};

// ── 2. Typical mid-complexity — Full-fidelity tour driver ───────────────────
const tourDriver: SimulationRequest = {
  launch: {
    ballSpeed: 75, // ~168 mph
    launchAngle: degToRad(14.1),
    spinRpm: 2700,
    clubSpeed: 51,
    spinAxisDeg: 1,
    launchDirDeg: 0,
    apexHint: null,
  },
  delivery: {
    attackAngleDeg: 2,
    dynamicLoftDeg: 14,
    clubPathDeg: 0,
    faceAngleDeg: 1,
    impactMm: [3, 0],
    lieAngleDeg: 0,
  },
  environment: calmEnv(),
  override: { landingAngleDeg: null },
  club: { category: ClubCategory.Driver, loftDeg: 10.5, rollPercent: 0.18 },
  fidelity: Fidelity.Full,
  ballType: 'tour',
};

// ── 3. Environment branch — windy, high-altitude, firm ground ───────────────
const windyHighland: SimulationRequest = {
  launch: {
    ballSpeed: 73,
    launchAngle: degToRad(13),
    spinRpm: 2900,
    clubSpeed: 50,
    spinAxisDeg: 0,
    launchDirDeg: 0,
    apexHint: null,
  },
  delivery: null,
  environment: {
    altitudeM: 1500, // thin air — less drag, more carry
    temperatureK: 288.15, // 15 °C
    windSpeed: 6, // ~13 mph headwind (windDirDeg 0 → headwind)
    windDirDeg: 0,
    firmness: Firmness.Firm,
    moisture: SurfaceMoisture.Dry,
    landingSurface: LandingSurface.Fairway,
    landingSlopeDeg: -2, // slight downhill landing zone → more roll
    crossSlopeDeg: 0,
    elevationDiffM: 0,
    seaLevelBaseline: false,
    humidity: 0.4,
    pressurePa: 101325,
  },
  override: { landingAngleDeg: null },
  club: { category: ClubCategory.Driver, loftDeg: 10.5, rollPercent: 0.2 },
  fidelity: Fidelity.Full,
  ballType: 'distance',
};

// ── 4. Shape + club branch — mid-iron draw with full delivery ───────────────
const midIronDraw: SimulationRequest = {
  launch: {
    ballSpeed: 62, // ~139 mph ball speed
    launchAngle: degToRad(16.5),
    spinRpm: 6200,
    clubSpeed: 41,
    spinAxisDeg: -5, // draw curvature (negative → left)
    launchDirDeg: -1,
    apexHint: null,
  },
  delivery: {
    attackAngleDeg: -3, // descending iron strike
    dynamicLoftDeg: 27,
    clubPathDeg: -3, // in-to-out
    faceAngleDeg: -1, // face slightly closed to path → draw
    impactMm: [0, 2], // slightly high on face
    lieAngleDeg: 0,
  },
  environment: calmEnv(),
  override: { landingAngleDeg: null },
  club: { category: ClubCategory.Iron, loftDeg: 27, rollPercent: 0.08 },
  fidelity: Fidelity.Full,
  ballType: 'urethane',
};

// ── 5. Club + surface + profile branch — wedge to a soft green, fade ────────
const wedgeToGreen: SimulationRequest = {
  launch: {
    ballSpeed: 44, // ~98 mph
    launchAngle: degToRad(28),
    spinRpm: 9500,
    clubSpeed: 32,
    spinAxisDeg: 4, // gentle fade
    launchDirDeg: 1,
    apexHint: null,
  },
  delivery: {
    attackAngleDeg: -5,
    dynamicLoftDeg: 47,
    clubPathDeg: 1,
    faceAngleDeg: 4,
    impactMm: [2, 0],
    lieAngleDeg: 0,
  },
  environment: {
    altitudeM: 0,
    temperatureK: 295.15, // 22 °C
    windSpeed: 2,
    windDirDeg: 90, // gentle crosswind
    firmness: Firmness.Soft,
    moisture: SurfaceMoisture.Normal,
    landingSurface: LandingSurface.Green, // high friction → minimal roll
    landingSlopeDeg: 0,
    crossSlopeDeg: 0,
    elevationDiffM: 0,
    seaLevelBaseline: false,
    humidity: 0.5,
    pressurePa: 101325,
  },
  override: { landingAngleDeg: 52 }, // steep descent expected for a wedge
  club: { category: ClubCategory.Wedge, loftDeg: 47, rollPercent: 0.04 },
  fidelity: Fidelity.Full,
  ballType: 'soft',
};

// ── 6. Maximal / stress — every lever pushed, extreme environment ───────────
const maximalStress: SimulationRequest = {
  launch: {
    ballSpeed: 90, // near the top of the validated envelope
    launchAngle: degToRad(15),
    spinRpm: 3200,
    clubSpeed: 64, // ~143 mph, near ceiling → smash ~1.41
    spinAxisDeg: -9, // strong hook curvature
    launchDirDeg: -2,
    apexHint: null,
  },
  delivery: {
    attackAngleDeg: 5,
    dynamicLoftDeg: 16,
    clubPathDeg: -6,
    faceAngleDeg: -2,
    impactMm: [-8, -3], // heel strike → gear-effect axis tilt
    lieAngleDeg: 2,
  },
  environment: {
    altitudeM: 2500, // high altitude, very thin air
    temperatureK: 303.15, // 30 °C hot day
    windSpeed: 12, // ~27 mph strong wind (quartering tail/cross)
    windDirDeg: 135,
    firmness: Firmness.Firm,
    moisture: SurfaceMoisture.Wet,
    landingSurface: LandingSurface.Fairway,
    landingSlopeDeg: -5, // downhill rollout
    crossSlopeDeg: 4, // lateral slope drift during roll
    elevationDiffM: -10, // playing 10 m downhill to the target
    seaLevelBaseline: false,
    humidity: 0.7,
    pressurePa: 101325,
  },
  override: { landingAngleDeg: null },
  club: { category: ClubCategory.Driver, loftDeg: 9, rollPercent: 0.22 },
  fidelity: Fidelity.Full,
  ballType: 'ionomer',
};

// ── Compare scenarios (exercise CompareScreen's simulate() path) ────────────
const driverScenarios: DemoScenarioRef[] = [
  { id: 'demo-sc-cal', label: 'Calm', request: structuredClone(tourDriver) },
  {
    id: 'demo-sc-wind',
    label: '5 m/s headwind',
    request: {
      ...structuredClone(tourDriver),
      environment: { ...tourDriver.environment, windSpeed: 5, windDirDeg: 0 },
    },
  },
  {
    id: 'demo-sc-tail',
    label: '5 m/s tailwind',
    request: {
      ...structuredClone(tourDriver),
      environment: { ...tourDriver.environment, windSpeed: 5, windDirDeg: 180 },
    },
  },
];

// ── Bag entries (exercise BagScreen's gapping path) ─────────────────────────
const typicalBag: BagEntry[] = [
  { clubName: 'Driver', category: ClubCategory.Driver, loftDeg: 10.5, carryM: 244, totalM: 285, estimated: false },
  { clubName: '3-Wood', category: ClubCategory.Wood, loftDeg: 15, carryM: 219, totalM: 245, estimated: false },
  { clubName: '5-Wood', category: ClubCategory.Wood, loftDeg: 18, carryM: 205, totalM: 224, estimated: false },
  { clubName: '4-Iron', category: ClubCategory.Iron, loftDeg: 24, carryM: 187, totalM: 199, estimated: false },
  { clubName: '6-Iron', category: ClubCategory.Iron, loftDeg: 31, carryM: 162, totalM: 170, estimated: false },
  { clubName: '8-Iron', category: ClubCategory.Iron, loftDeg: 39, carryM: 140, totalM: 145, estimated: false },
  { clubName: 'PW', category: ClubCategory.Wedge, loftDeg: 47, carryM: 118, totalM: 120, estimated: false },
  { clubName: 'SW', category: ClubCategory.Wedge, loftDeg: 56, carryM: 88, totalM: 89, estimated: false },
];

const now = 1_700_000_000_000;

// ── A small session (exercise SessionsScreen aggregates) ────────────────────
const driverSession: Session = {
  id: 'demo-session-driver',
  name: 'Range — Driver',
  type: SessionType.Outdoor,
  clubCategory: ClubCategory.Driver,
  shots: [
    { id: 's1', request: structuredClone(tourDriver), carryM: 244, totalM: 285, excluded: false },
    { id: 's2', request: structuredClone(tourDriver), carryM: 249, totalM: 291, excluded: false },
    { id: 's3', request: structuredClone(tourDriver), carryM: 241, totalM: 281, excluded: true },
  ],
  avgCarryM: 246.5,
  avgTotalM: 288,
  ballSpeedVariance: 4,
  createdAt: now,
  updatedAt: now,
};

/**
 * The full ordered list of demo datasets surfaced in the Load Demo picker.
 */
export const DEMO_DATASETS: readonly DemoDataset[] = [
  {
    id: 'simple-driver',
    name: 'Simple Driver (Basic)',
    description:
      'Minimal case — Basic-fidelity driver on a calm day. Fast reduced model with no environment effects.',
    state: { request: simpleDriver },
  },
  {
    id: 'tour-driver',
    name: 'Tour Driver — Full Fidelity',
    description:
      'Typical mid-complexity case — full 3D flight, measured delivery, with a populated bag, a driver session, and compare scenarios.',
    state: {
      request: structuredClone(tourDriver),
      bag: typicalBag,
      sessions: [structuredClone(driverSession)],
      scenarios: driverScenarios.map((s) => ({ ...s, request: structuredClone(s.request) })),
    },
  },
  {
    id: 'windy-highland',
    name: 'Windy Highland Driver',
    description:
      'Environment branch — 1500 m altitude with a 6 m/s headwind over firm, dry turf. Demonstrates thin-air + wind effects.',
    state: { request: windyHighland },
  },
  {
    id: 'mid-iron-draw',
    name: 'Mid-Iron Draw',
    description:
      'Shape + club branch — 6-iron with an in-to-out path producing draw curvature. Full delivery inputs present.',
    state: { request: midIronDraw },
  },
  {
    id: 'wedge-to-green',
    name: 'Pitching Wedge to Green',
    description:
      'Club + surface + profile branch — high-spin wedge into a soft green with a steep landing-angle override and a gentle fade.',
    state: { request: wedgeToGreen },
  },
  {
    id: 'maximal-stress',
    name: 'Maximal Stress Conditions',
    description:
      'Stress case — near-envelope ball speed, 2500 m altitude, strong quartering wind, downhill target, wet firm turf, heel strike.',
    state: { request: maximalStress },
  },
] as const;

export { BallType };
